import { useState } from "react";
import BookingCalendar from "../components/BookingCalendar";
import { ChevronLeftIcon } from "@heroicons/react/outline";
import { useNavigate } from "react-router-dom";

const inputCls =
  "w-full bg-[#0d1424] border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-slate-600 focus:outline-none focus:ring-2 focus:ring-pink-500/40 focus:border-pink-500/30 transition";

// ── Shared input style ──
const selectCls =
  "w-full bg-[#0d1424] border border-white/10 rounded-xl px-4 py-3 text-sm text-white appearance-none focus:outline-none focus:ring-2 focus:ring-pink-500/40 focus:border-pink-500/30 transition cursor-pointer";

// ── Field wrapper with floating label style ──
const Field = ({ label, required, children }) => (
  <div className="space-y-1.5">
    <label className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-widest text-slate-400">
      {label}
      {required && <span className="text-pink-500 text-[10px]">*</span>}
    </label>
    <div className="relative">
      {children}
      {/* Chevron for selects */}
      <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-500">
        <svg
          className="w-4 h-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </div>
    </div>
  </div>
);

const Step1BookingInfo = ({
  form,
  setForm,
  promoters,
  artists,
  companies,
  onNext,
}) => {
  const [error, setError] = useState("");

  const navigate = useNavigate();

  const validate = () => {
    if (
      !form.promoter ||
      !form.artist ||
      !form.artist_fee_company ||
      !form.booking_fee_company ||
      !form.booking_date
    ) {
      setError("All * fields are required.");
      return false;
    }
    setError("");
    return true;
  };

  const handleNext = () => {
    if (!validate()) return;
    onNext();
  };

  // Count filled fields for progress indicator
  const fields = [
    form.promoter,
    form.artist,
    form.artist_fee_company,
    form.booking_fee_company,
    form.booking_date,
    form.contract_no,
  ];
  const filledCount = fields.filter(Boolean).length;
  const progressPct = (filledCount / fields.length) * 100;

  return (
    <div className="space-y-8">
      {/* ── Step header ── */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[10px] font-semibold uppercase text-pink-500 bg-pink-500/10 border border-pink-500/20 px-3 py-1 rounded-full">
              Step 1 of 3
            </span>
          </div>
          <h2 className="text-xl font-semibold mt-3 text-white">
            Booking Information
          </h2>
          <button
            onClick={() => navigate(-1)}
            className="flex mt-3 items-center gap-1.5 text-sm text-slate-400 hover:text-white transition"
          >
            <ChevronLeftIcon className="w-4 h-4" />
            Back to Bookings
          </button>
        </div>

        {/* Progress pill */}
        <div className="shrink-0 flex flex-col items-end gap-1.5">
          <span className="text-xs text-slate-500 tabular-nums">
            {filledCount} / {fields.length} fields
          </span>
          <div className="w-28 h-1.5 bg-slate-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-pink-600 to-rose-500 rounded-full transition-all duration-500"
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>
      </div>

      {/* ── Main grid ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* LEFT — Form fields */}
        <div className="bg-slate-900/60 rounded-2xl p-6 space-y-5">
          {/* Promoter */}
          <Field label="Promoter" required>
            <select
              value={form.promoter || ""}
              onChange={(e) => {
                setForm({ ...form, promoter: e.target.value });
                setError("");
              }}
              className={selectCls}
            >
              <option value="">Select promoter…</option>
              {(promoters || []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </Field>

          {/* Artist */}
          <Field label="Artist" required>
            <select
              value={form.artist || ""}
              onChange={(e) => {
                setForm({ ...form, artist: e.target.value });
                setError("");
              }}
              className={selectCls}
            >
              <option value="">Select artist…</option>
              {(artists || []).map((a) => (
                <option key={a.id} value={a.id}>
                  {a.real_name}
                </option>
              ))}
            </select>
          </Field>

          {/* Artist Fee Company */}
          <Field label="Artist Fee Company" required>
            <select
              value={form.artist_fee_company || ""}
              onChange={(e) => {
                setForm({ ...form, artist_fee_company: e.target.value });
                setError("");
              }}
              className={selectCls}
            >
              <option value="">Select company…</option>
              {(companies || []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </Field>

          {/* Booking Fee Company */}
          <Field label="Booking Fee Company" required>
            <select
              value={form.booking_fee_company || ""}
              onChange={(e) => {
                setForm({ ...form, booking_fee_company: e.target.value });
                setError("");
              }}
              className={selectCls}
            >
              <option value="">Select company…</option>
              {(companies || []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </Field>

          {/* Contract No */}
          {/* Contract No */}
          <div className="space-y-1.5">
            <label className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-widest text-slate-400">
              Contract No
            </label>
            <input
              type="text"
              value={form.contract_no || ""}
              onChange={(e) => {
                setForm({ ...form, contract_no: e.target.value });
                setError("");
              }}
              className={inputCls}
            />
          </div>

          {/* Selected date display */}
          {form.booking_date && (
            <div className="flex items-center gap-3 bg-pink-500/8 border border-pink-500/20 rounded-xl px-4 py-3 mt-1">
              <div className="w-2 h-2 rounded-full bg-pink-500 shrink-0" />
              <div>
                <p className="text-[10px] uppercase tracking-widest text-pink-400/70 font-medium">
                  Selected Date
                </p>
                <p className="text-sm text-white font-medium mt-0.5">
                  {new Date(form.booking_date).toLocaleDateString("en-GB", {
                    weekday: "long",
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  })}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* RIGHT — Calendar */}
        <div className="bg-slate-900/60 rounded-2xl overflow-hidden">
          <div className="px-5 py-4">
            <p className="text-xs uppercase tracking-widest text-slate-500 font-medium">
              Booking Date
            </p>
            <p className="text-[11px] text-slate-600 mt-0.5">
              {form.booking_date
                ? "Date selected — change by clicking another day"
                : "Click a date to select it"}
            </p>
          </div>
          <div className="p-2">
            <BookingCalendar
              artistId={form.artist}
              selectedDate={form.booking_date}
              setSelectedDate={(date) => {
                setForm({ ...form, booking_date: date });
                setError("");
              }}
            />
          </div>
        </div>
      </div>

      {/* ── Footer ── */}
      <div className="flex flex-col items-center gap-3 pt-2">
        {error && (
          <div className="flex items-center gap-2 text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-2.5">
            <svg
              className="w-4 h-4 shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            {error}
          </div>
        )}

        <button
          onClick={handleNext}
          className="group flex items-center gap-2.5 bg-pink-600 hover:bg-pink-700 active:bg-pink-800 px-10 py-3 rounded-xl text-white font-medium text-sm transition-all duration-150 shadow-lg shadow-pink-900/30"
        >
          Continue to Next Step
          <svg
            className="w-4 h-4 transition-transform duration-150 group-hover:translate-x-0.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5l7 7-7 7"
            />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default Step1BookingInfo;
